import type { HealthReport } from "@agent-todo/shared"
import { useQuery } from "@tanstack/react-query"
import { Try } from "functype"

import { getServiceUrl, MONITORED_SERVICES, type ServiceId } from "@/config/services"
import { env } from "@/lib/env"

export interface ServiceHealthData {
  healthData: HealthReport | null
  isLoading: boolean
  error: Error | null
  url: string
}

export interface MultiServiceHealthData {
  services: Record<ServiceId, ServiceHealthData>
  overallStatus: "healthy" | "degraded" | "unhealthy"
  isLoading: boolean
}

/**
 * Fetch health data from a single service endpoint
 */
async function fetchServiceHealth(url: string, token?: string): Promise<HealthReport> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(token !== undefined && token !== "" && { Authorization: `Bearer ${token}` }),
  }

  const response = await fetch(`${url}/health/detailed`, {
    headers,
  })

  if (!response.ok) {
    throw new Error(`Health check failed: ${response.status} ${response.statusText}`)
  }

  return response.json() as Promise<HealthReport>
}

/**
 * Determine if we're in production based on environment
 */
function isProduction(): boolean {
  // Check if NOT dev environment (dev URLs contain "dev-" or "dev.")
  const apiUrl = env.VITE_API_URL
  const appUrl = env.VITE_APP_URL ?? ""
  const isDev = apiUrl.includes("dev-") || apiUrl.includes("dev.") || appUrl.includes("dev.")
  return import.meta.env.PROD && !isDev
}

/**
 * Hook to fetch health data from all monitored services
 */
export function useMultiServiceHealth(autoRefresh = true): MultiServiceHealthData {
  const isProd = isProduction()
  const token = env.VITE_MONITORING_TOKEN

  const { data, isLoading } = useQuery({
    queryKey: ["multi-service-health", isProd],
    queryFn: async () => {
      // Fetch health from all services in parallel
      const serviceIds = Object.keys(MONITORED_SERVICES) as ServiceId[]
      const results = await Promise.allSettled(
        serviceIds.map(async (serviceId) => {
          const url = getServiceUrl(serviceId, isProd)
          const attempt = await Try.fromPromise(fetchServiceHealth(url, token))
          type Row = { serviceId: ServiceId; healthData: HealthReport | null; error: Error | null; url: string }
          return attempt.fold<Row>(
            (error) => ({
              serviceId,
              healthData: null,
              error: error as Error,
              url,
            }),
            (healthData) => ({
              serviceId,
              healthData,
              error: null,
              url,
            }),
          )
        }),
      )

      // Transform results into service map
      const services = Object.fromEntries(
        results.flatMap((result, index): Array<[ServiceId, ServiceHealthData]> => {
          const serviceId = serviceIds[index]
          if (serviceId === undefined) return []

          if (result.status === "fulfilled") {
            return [
              [
                serviceId,
                {
                  healthData: result.value.healthData,
                  error: result.value.error,
                  isLoading: false,
                  url: result.value.url,
                },
              ],
            ]
          }
          return [
            [
              serviceId,
              {
                healthData: null,
                error: result.reason as Error,
                isLoading: false,
                url: getServiceUrl(serviceId, isProd),
              },
            ],
          ]
        }),
      ) as Record<ServiceId, ServiceHealthData>

      return services
    },
    refetchInterval: autoRefresh ? 30000 : false, // Refetch every 30 seconds
    retry: 2,
    staleTime: 10000, // Consider data stale after 10 seconds
  })

  // Calculate overall status
  const overallStatus = calculateOverallStatus(data)

  return {
    services: data ?? ({} as Record<ServiceId, ServiceHealthData>),
    overallStatus,
    isLoading,
  }
}

/**
 * Calculate overall system status based on individual service health
 */
function calculateOverallStatus(
  services: Record<ServiceId, ServiceHealthData> | undefined,
): "healthy" | "degraded" | "unhealthy" {
  if (!services || Object.keys(services).length === 0) {
    return "unhealthy"
  }

  const serviceStatuses = Object.values(services).map((service) => {
    if (!service.healthData) {
      return "unhealthy"
    }
    return service.healthData.overall
  })

  const hasUnhealthy = serviceStatuses.includes("unhealthy")
  const hasDegraded = serviceStatuses.includes("degraded")

  if (hasUnhealthy) {
    return "unhealthy"
  }

  if (hasDegraded) {
    return "degraded"
  }

  return "healthy"
}
